/**
 * Object-mode operations slice for the Zustand store.
 *
 * Contains transform application, origin manipulation, duplicate
 * dispatch, and object library management.
 */

import * as THREE from "three/webgpu";
import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import { getBone } from "../mannequin/boneRegistry.ts";
import { cursorSpawnPosition } from "../modeling/cursor3d.ts";
import {
  hasVertexSelection,
  vertexCentroid,
} from "../modeling/editSelection.ts";
import {
  applyFaceShadingOverride,
  concatMeshes,
  mergeByDistance as meshMergeByDistance,
  recomputeLoopNormals as meshRecomputeLoopNormals,
  resolveAutoSmoothAngle,
} from "../modeling/meshOps.ts";
import {
  createLibraryEntryId,
  type LibraryEntry,
  saveLibrary,
} from "../modeling/objectLibrary.ts";
import {
  composeProvenance,
  inheritPerVertexAttributes,
  inheritTopologyAttributes,
  provenanceFromJoinAppend,
} from "../modeling/provenance.ts";
import {
  topologyFromTriangles,
  triangulateTopologyForRendering,
} from "../modeling/topology.ts";
import { normalizeTopologyBackedGeometry } from "../modeling/topologyBackedGeometry.ts";
import type { SceneObject } from "../modeling/types.ts";
import {
  asLoopNormals,
  asRenderTriangleIndices,
  asSharedTriangleIndices,
  cloneRenderTriangleIndices,
  createDefaultMaterialSlots,
  createSceneObjectId,
  DEFAULT_MATERIAL,
  getPrimaryMaterialId,
  isImagePlaneObject,
  isLightObject,
  isMannequinObject,
} from "../modeling/types.ts";
import { unwrapBoxProject } from "../modeling/uvUnwrap.ts"; // used directly for join
import { pruneSceneSketch } from "../sketch/sceneSketch.ts";
import { getMannequinSurfaceSnapshot } from "../viewport/mannequinSurfaceSnapshots.ts";
import {
  buildModifierEvaluationRequest,
  evaluateModifierStackAsync,
} from "../viewport/modifierApply.ts";
import { sceneObjectGeometryCacheKey } from "../viewport/modifiers/cacheKeys.ts";
import {
  buildSceneObjectModifierGeometry,
  buildSceneObjectNameMap,
  mergeModifierStack,
} from "../viewport/modifiers/evaluation.ts";
import {
  type ModifierGeometry,
  resolveModifierGeometryTopology,
} from "../viewport/modifiers/modifierTypes.ts";
import {
  buildSceneObjectWorldNameMap,
  getSceneObjectWorldTransform,
  removeCollectionMembers,
} from "./collectionGraph.ts";
import { resolveCollectionCutterRef } from "./cutterCollections.ts";
import type { EulerTransform } from "./groupTransform.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import {
  filterExistingSelectionTargets,
  selectionToLeafObjectIds,
} from "./objectSelection.ts";
import {
  addSceneObject,
  addSceneObjects,
  patchSceneObject,
} from "./sceneObjectMap.ts";
import { duplicateSelectionInEditMode } from "./storeEditOps/index.ts";
import type {
  AppState,
  InteractionMode,
  ModifierOverride,
} from "./storeTypes.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

function sceneObjectEulerTransform(obj: SceneObject): EulerTransform {
  return {
    position: [obj.position[0], obj.position[1], obj.position[2]],
    rotation: [obj.rotation[0], obj.rotation[1], obj.rotation[2]],
    scale: [obj.scale[0], obj.scale[1], obj.scale[2]],
  };
}

function removeCutterBooleanOverrides(
  overrides: readonly ModifierOverride[],
  cutterNames: ReadonlySet<string>,
): ModifierOverride[] {
  return overrides.filter((override) => {
    const modifier = override.runtimeModifier;
    return !(
      modifier?.kind === "boolean" &&
      modifier.targetObjectName &&
      cutterNames.has(modifier.targetObjectName)
    );
  });
}

function retargetModifierObjectName(
  modifier: BlendModifier,
  oldName: string,
  newName: string,
): BlendModifier {
  if (
    !("targetObjectName" in modifier) ||
    modifier.targetObjectName !== oldName
  ) {
    return modifier;
  }
  return { ...modifier, targetObjectName: newName } as BlendModifier;
}

function retargetModifierOverrideObjectName(
  override: ModifierOverride,
  oldName: string,
  newName: string,
): ModifierOverride {
  let next = override;
  if (override.runtimeModifier) {
    const runtimeModifier = retargetModifierObjectName(
      override.runtimeModifier,
      oldName,
      newName,
    );
    if (runtimeModifier !== override.runtimeModifier) {
      next = { ...next, runtimeModifier };
    }
  }
  if (override.props?.targetObjectName === oldName) {
    next = {
      ...next,
      props: { ...override.props, targetObjectName: newName },
    };
  }
  return next;
}

function retargetModifierOverridesObjectName(
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>,
  oldName: string,
  newName: string,
): Map<string, ModifierOverride[]> | null {
  let nextOverrides: Map<string, ModifierOverride[]> | null = null;
  for (const [objectId, overrides] of modifierOverrides) {
    let changed = false;
    const next = overrides.map((override) => {
      const updated = retargetModifierOverrideObjectName(
        override,
        oldName,
        newName,
      );
      if (updated !== override) changed = true;
      return updated;
    });
    if (!changed) continue;
    nextOverrides ??= new Map(
      [...modifierOverrides].map(([id, value]) => [id, [...value]]),
    );
    nextOverrides.set(objectId, next);
  }
  return nextOverrides;
}

function buildDeleteCutterObjectsPatch(
  state: AppState,
  collectionId: string,
  cutterObjectIdsToRemove: ReadonlySet<string>,
  options: { selectHostAfterDelete?: boolean } = {},
): Partial<AppState> | null {
  const collection = state.collections.get(collectionId);
  const cutterRef = collection
    ? resolveCollectionCutterRef(
        collection,
        state.sceneObjects,
        state.modifierOverrides,
      )
    : null;
  if (!collection || !cutterRef) return null;

  const hostObject = state.sceneObjects.get(cutterRef.hostObjectId);
  if (!hostObject) return null;

  const removedObjectIds = new Set<string>();
  for (const objectId of cutterObjectIdsToRemove) {
    if (objectId !== hostObject.id && cutterRef.cutterObjectIds.has(objectId)) {
      removedObjectIds.add(objectId);
    }
  }
  if (removedObjectIds.size === 0) return null;

  const remainingCutterObjectIds = new Set(cutterRef.cutterObjectIds);
  for (const objectId of removedObjectIds) {
    remainingCutterObjectIds.delete(objectId);
  }

  const cutterNames = new Set<string>();
  for (const objectId of removedObjectIds) {
    const object = state.sceneObjects.get(objectId);
    if (object) cutterNames.add(object.name);
  }

  const sceneObjects = new Map(state.sceneObjects);
  const collections = new Map(state.collections);

  if (remainingCutterObjectIds.size === 0) {
    const hostWorld =
      getSceneObjectWorldTransform(
        state.sceneObjects,
        state.collections,
        hostObject.id,
      ) ?? sceneObjectEulerTransform(hostObject);

    const preservedMemberWorldTransforms = new Map<string, EulerTransform>();
    for (const objectId of collection.objectIds) {
      if (removedObjectIds.has(objectId)) continue;
      const world = getSceneObjectWorldTransform(
        state.sceneObjects,
        state.collections,
        objectId,
      );
      if (world) preservedMemberWorldTransforms.set(objectId, world);
    }
    preservedMemberWorldTransforms.set(hostObject.id, hostWorld);

    for (const [objectId, world] of preservedMemberWorldTransforms) {
      const object = sceneObjects.get(objectId);
      if (!object) continue;
      sceneObjects.set(objectId, {
        ...object,
        position: world.position,
        rotation: world.rotation,
        scale: world.scale,
      });
    }
    collections.delete(collectionId);
  } else {
    removeCollectionMembers(collections, collectionId, removedObjectIds);
  }

  for (const objectId of removedObjectIds) {
    sceneObjects.delete(objectId);
  }

  const modifierOverrides = new Map(state.modifierOverrides);
  const hostOverrides = modifierOverrides.get(hostObject.id) ?? [];
  const nextHostOverrides = removeCutterBooleanOverrides(
    hostOverrides,
    cutterNames,
  );
  if (nextHostOverrides.length === 0) {
    modifierOverrides.delete(hostObject.id);
  } else {
    modifierOverrides.set(hostObject.id, nextHostOverrides);
  }
  for (const objectId of removedObjectIds) {
    modifierOverrides.delete(objectId);
  }

  const visibility = new Map(state.visibility);
  const renderVisibility = new Map(state.renderVisibility);
  const modifierUICollapsed = new Map(state.modifierUICollapsed);
  modifierUICollapsed.delete(hostObject.id);
  for (const objectId of removedObjectIds) {
    visibility.delete(objectId);
    renderVisibility.delete(objectId);
    modifierUICollapsed.delete(objectId);
  }

  const sketch = pruneSceneSketch(state.sketch, {
    removedObjectIds,
  });
  const selectedObjectTargets = filterExistingSelectionTargets(
    sceneObjects,
    state.blendFiles,
    collections,
    state.selectedObjectTargets,
  );
  const fallbackSelection = [{ kind: "sceneObject" as const, id: hostObject.id }];

  return {
    sceneObjects,
    collections,
    modifierOverrides,
    visibility,
    renderVisibility,
    modifierUICollapsed,
    sketch,
    selectedObjectTargets: options.selectHostAfterDelete
      ? fallbackSelection
      : selectedObjectTargets.length > 0
        ? selectedObjectTargets
        : fallbackSelection,
    mode: "transform" as InteractionMode,
    editMode:
      state.editMode && removedObjectIds.has(state.editMode.objectId)
        ? null
        : state.editMode,
    activeEditSession:
      state.editMode && removedObjectIds.has(state.editMode.objectId)
        ? null
        : state.activeEditSession,
  };
}

function runtimeMannequinModifierSources(
  modifiers: readonly BlendModifier[],
  sceneObjectsByName: ReadonlyMap<string, SceneObject>,
): {
  runtimeSourceGeometries?: Record<string, ModifierGeometry>;
  runtimeSourceCacheKeys?: Map<string, string>;
} {
  const runtimeSourceGeometries: Record<string, ModifierGeometry> = {};
  const runtimeSourceCacheKeys = new Map<string, string>();

  for (const modifier of modifiers) {
    if (
      !modifier.enabled ||
      modifier.kind !== "shrinkwrap" ||
      !modifier.targetObjectName
    ) {
      continue;
    }
    const target = sceneObjectsByName.get(modifier.targetObjectName);
    if (!target || !isMannequinObject(target)) continue;
    const snapshot = getMannequinSurfaceSnapshot(target.id);
    if (!snapshot) continue;
    runtimeSourceGeometries[modifier.targetObjectName] = snapshot.geometry;
    runtimeSourceCacheKeys.set(modifier.targetObjectName, snapshot.cacheKey);
  }

  return {
    runtimeSourceGeometries:
      Object.keys(runtimeSourceGeometries).length > 0
        ? runtimeSourceGeometries
        : undefined,
    runtimeSourceCacheKeys:
      runtimeSourceCacheKeys.size > 0 ? runtimeSourceCacheKeys : undefined,
  };
}

function modifierGeometryBakePatch(
  obj: SceneObject,
  result: ModifierGeometry,
): Partial<SceneObject> {
  const newVertices = new Float32Array(result.positions);
  const faceAttributes = result.faceAttributes;
  const modifierTopology = result.topology
    ? resolveModifierGeometryTopology(result)
    : undefined;
  const normalized = normalizeTopologyBackedGeometry(
    newVertices,
    modifierTopology ??
      topologyFromTriangles(asSharedTriangleIndices(result.indices)),
    {
      uvs: result.uvs,
      shading: obj.shading,
      autoSmoothAngle: obj.autoSmoothAngle,
      generateMissingUvs: false,
    },
  );
  applyFaceShadingOverride(
    normalized.loopNormals,
    newVertices,
    normalized.topology,
    faceAttributes,
  );

  const bakedScaffold: SceneObject = {
    ...obj,
    vertices: newVertices,
    loopNormals: normalized.loopNormals,
    indices: normalized.indices,
    topology: normalized.topology,
    uvs: normalized.uvs,
    faceAttributes,
  };
  let bakedObject = inheritPerVertexAttributes(
    obj,
    bakedScaffold,
    result.provenance,
  );
  if (modifierTopology) {
    bakedObject = {
      ...bakedObject,
      topology: inheritTopologyAttributes(
        obj.topology,
        bakedObject.topology,
        result.provenance,
      ),
    };
  }

  return {
    vertices: bakedObject.vertices,
    loopNormals: bakedObject.loopNormals,
    indices: bakedObject.indices,
    topology: bakedObject.topology,
    uvs: bakedObject.uvs,
    vertexGroups: bakedObject.vertexGroups,
    skinRadii: bakedObject.skinRadii,
    baseVertices: bakedObject.baseVertices,
    faceAttributes: bakedObject.faceAttributes,
  };
}

export function originToPositionPatch(
  obj: SceneObject,
  worldPoint: [number, number, number],
): Partial<SceneObject> | null {
  // Mannequins keep their skinned geometry in the GLB, so moving the
  // origin becomes a counter-offset on `originOffset`.
  if (obj.objectKind === "mannequin") {
    const invMat = new THREE.Matrix4()
      .compose(
        new THREE.Vector3(0, 0, 0),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(...obj.rotation)),
        new THREE.Vector3(...obj.scale),
      )
      .invert();
    const deltaWorld = new THREE.Vector3(
      worldPoint[0] - obj.position[0],
      worldPoint[1] - obj.position[1],
      worldPoint[2] - obj.position[2],
    );
    const localDelta = deltaWorld.applyMatrix4(invMat);

    const ox = obj.originOffset?.[0] ?? 0;
    const oy = obj.originOffset?.[1] ?? 0;
    const oz = obj.originOffset?.[2] ?? 0;

    return {
      position: [worldPoint[0], worldPoint[1], worldPoint[2]] as [
        number,
        number,
        number,
      ],
      originOffset: [
        ox - localDelta.x,
        oy - localDelta.y,
        oz - localDelta.z,
      ] as [number, number, number],
    };
  }

  if (obj.vertices.length === 0) {
    return {
      position: [worldPoint[0], worldPoint[1], worldPoint[2]] as [
        number,
        number,
        number,
      ],
    };
  }

  const deltaWorld = new THREE.Vector3(
    worldPoint[0] - obj.position[0],
    worldPoint[1] - obj.position[1],
    worldPoint[2] - obj.position[2],
  );
  const invMat = new THREE.Matrix4()
    .compose(
      new THREE.Vector3(0, 0, 0),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(...obj.rotation)),
      new THREE.Vector3(...obj.scale),
    )
    .invert();
  const localDelta = deltaWorld.applyMatrix4(invMat);

  const vertCount = obj.vertices.length / 3;
  const newVerts = new Float32Array(obj.vertices);
  for (let i = 0; i < vertCount; i++) {
    newVerts[i * 3] -= localDelta.x;
    newVerts[i * 3 + 1] -= localDelta.y;
    newVerts[i * 3 + 2] -= localDelta.z;
  }

  return {
    vertices: newVerts,
    loopNormals: meshRecomputeLoopNormals(
      newVerts,
      obj.topology,
      resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
    ),
    position: [worldPoint[0], worldPoint[1], worldPoint[2]] as [
      number,
      number,
      number,
    ],
  };
}

/**
 * Bake an arbitrary Euler TRS into an object's vertex data and return a
 * patch that also resets the object's local TRS to identity. Returns null
 * when no change is needed (identity transform, or mannequin already at
 * identity). Mannequins cannot bake into verts — the patch only normalizes
 * their TRS.
 *
 * When `bakeMatrix` is present in the result, the caller MUST also feed it
 * to `pruneSceneSketch` (under `bakedTransforms`) so projected sketch strokes
 * attached to this object stay locked to the surface. Skipping that step
 * leaves strokes anchored to the pre-bake local frame, drifting visibly
 * relative to the now-rebaked mesh.
 */
export interface BakeTransformResult {
  patch: Partial<SceneObject>;
  bakeMatrix?: THREE.Matrix4;
}

export function bakeTransformIntoVerticesPatch(
  obj: SceneObject,
  transform: {
    position: readonly [number, number, number];
    rotation: readonly [number, number, number];
    scale: readonly [number, number, number];
  },
): BakeTransformResult | null {
  const [px, py, pz] = transform.position;
  const [rx, ry, rz] = transform.rotation;
  const [sx, sy, sz] = transform.scale;
  const isIdentity =
    px === 0 &&
    py === 0 &&
    pz === 0 &&
    rx === 0 &&
    ry === 0 &&
    rz === 0 &&
    sx === 1 &&
    sy === 1 &&
    sz === 1;
  if (isIdentity) return null;

  const resetTRS = {
    position: [0, 0, 0] as [number, number, number],
    rotation: [0, 0, 0] as [number, number, number],
    scale: [1, 1, 1] as [number, number, number],
  };

  if (obj.objectKind === "mannequin") return { patch: resetTRS };
  if (obj.vertices.length === 0) return { patch: resetTRS };

  const mat = new THREE.Matrix4().compose(
    new THREE.Vector3(px, py, pz),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)),
    new THREE.Vector3(sx, sy, sz),
  );

  const newVerts = new Float32Array(obj.vertices);
  const v = new THREE.Vector3();
  const vertCount = newVerts.length / 3;
  for (let i = 0; i < vertCount; i++) {
    v.set(newVerts[i * 3], newVerts[i * 3 + 1], newVerts[i * 3 + 2]);
    v.applyMatrix4(mat);
    newVerts[i * 3] = v.x;
    newVerts[i * 3 + 1] = v.y;
    newVerts[i * 3 + 2] = v.z;
  }

  const newNormals = meshRecomputeLoopNormals(
    newVerts,
    obj.topology,
    resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
  );

  return {
    patch: {
      vertices: newVerts,
      loopNormals: newNormals,
      ...resetTRS,
    },
    /** The matrix that was multiplied into every vertex. Callers should pass
     *  this through `pruneSceneSketch`'s `bakedTransforms` map so projected
     *  sketch strokes attached to this object get their bind-pose `point.p`
     *  baked too — otherwise strokes float at the pre-bake position. */
    bakeMatrix: mat,
  };
}

/** Object-mode store methods — call createObjectOps(set, get) inside the store factory. */
export function createObjectOps(set: Set_, get: Get_) {
  return {
    applyTransform: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;
      const result = bakeTransformIntoVerticesPatch(obj, {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      });
      if (!result) return;
      get().pushUndo();
      const patch: Partial<SceneObject> = result.patch;
      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, patch),
        ...(result.bakeMatrix
          ? {
              sketch: pruneSceneSketch(get().sketch, {
                bakedTransforms: new Map([[objectId, result.bakeMatrix]]),
              }),
            }
          : {}),
      });
    },

    setOriginToGeometry: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;

      // Mannequins: vertex data lives in the GLB and can't be shifted.
      // Compute the geometry centroid from the extracted bind-pose vertices,
      // move the origin to that world-space position, and store a local-space
      // counter-offset so the mesh stays in place visually.
      if (obj.objectKind === "mannequin") {
        const centroid = vertexCentroid(obj.vertices);
        if (!centroid) return;
        let [cx, cy, cz] = centroid;

        // Add existing originOffset to get the true local centroid
        const ox = obj.originOffset?.[0] ?? 0;
        const oy = obj.originOffset?.[1] ?? 0;
        const oz = obj.originOffset?.[2] ?? 0;
        cx += ox;
        cy += oy;
        cz += oz;

        // Transform centroid to world space using rotation + scale
        const mat = new THREE.Matrix4().compose(
          new THREE.Vector3(0, 0, 0),
          new THREE.Quaternion().setFromEuler(new THREE.Euler(...obj.rotation)),
          new THREE.Vector3(...obj.scale),
        );
        const worldOffset = new THREE.Vector3(cx, cy, cz).applyMatrix4(mat);

        get().pushUndo();
        set({
          sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
            position: [
              obj.position[0] + worldOffset.x,
              obj.position[1] + worldOffset.y,
              obj.position[2] + worldOffset.z,
            ] as [number, number, number],
            originOffset: [-cx, -cy, -cz] as [number, number, number],
          }),
        });
        return;
      }

      const centroid = vertexCentroid(obj.vertices);
      if (!centroid) return;
      const [cx, cy, cz] = centroid;

      const vertCount = obj.vertices.length / 3;
      const newVerts = new Float32Array(obj.vertices);
      for (let i = 0; i < vertCount; i++) {
        newVerts[i * 3] -= cx;
        newVerts[i * 3 + 1] -= cy;
        newVerts[i * 3 + 2] -= cz;
      }

      const mat = new THREE.Matrix4().compose(
        new THREE.Vector3(0, 0, 0),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(...obj.rotation)),
        new THREE.Vector3(...obj.scale),
      );
      const worldOffset = new THREE.Vector3(cx, cy, cz).applyMatrix4(mat);

      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
          vertices: newVerts,
          loopNormals: meshRecomputeLoopNormals(
            newVerts,
            obj.topology,
            resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
          ),
          position: [
            obj.position[0] + worldOffset.x,
            obj.position[1] + worldOffset.y,
            obj.position[2] + worldOffset.z,
          ] as [number, number, number],
        }),
      });
    },

    setOriginToSelection: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj || obj.objectKind === "mannequin") return;
      const em = get().editMode;
      if (!em || em.objectId !== objectId || !hasVertexSelection(em)) return;

      const centroid = vertexCentroid(obj.vertices, em.selectedVertices);
      if (!centroid) return;
      const [cx, cy, cz] = centroid;

      // Shift all vertices so the selection centroid becomes the local origin
      const vertCount = obj.vertices.length / 3;
      const newVerts = new Float32Array(obj.vertices);
      for (let i = 0; i < vertCount; i++) {
        newVerts[i * 3] -= cx;
        newVerts[i * 3 + 1] -= cy;
        newVerts[i * 3 + 2] -= cz;
      }

      // Convert local centroid offset to world space via rotation+scale
      const mat = new THREE.Matrix4().compose(
        new THREE.Vector3(0, 0, 0),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(...obj.rotation)),
        new THREE.Vector3(...obj.scale),
      );
      const worldOffset = new THREE.Vector3(cx, cy, cz).applyMatrix4(mat);

      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
          vertices: newVerts,
          loopNormals: meshRecomputeLoopNormals(
            newVerts,
            obj.topology,
            resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
          ),
          position: [
            obj.position[0] + worldOffset.x,
            obj.position[1] + worldOffset.y,
            obj.position[2] + worldOffset.z,
          ] as [number, number, number],
        }),
      });
    },

    setOriginToWorld: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;

      // Mannequins: move origin to world origin, apply counter-offset to keep mesh in place
      if (obj.objectKind === "mannequin") {
        // Transform current position from world to local space
        const invMat = new THREE.Matrix4()
          .compose(
            new THREE.Vector3(0, 0, 0),
            new THREE.Quaternion().setFromEuler(
              new THREE.Euler(...obj.rotation),
            ),
            new THREE.Vector3(...obj.scale),
          )
          .invert();
        const localPos = new THREE.Vector3(...obj.position).applyMatrix4(
          invMat,
        );

        const ox = obj.originOffset?.[0] ?? 0;
        const oy = obj.originOffset?.[1] ?? 0;
        const oz = obj.originOffset?.[2] ?? 0;

        get().pushUndo();
        set({
          sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
            position: [0, 0, 0] as [number, number, number],
            originOffset: [
              ox + localPos.x,
              oy + localPos.y,
              oz + localPos.z,
            ] as [number, number, number],
          }),
        });
        return;
      }

      if (obj.vertices.length === 0) return;

      const [px, py, pz] = obj.position;

      const invMat = new THREE.Matrix4()
        .compose(
          new THREE.Vector3(0, 0, 0),
          new THREE.Quaternion().setFromEuler(new THREE.Euler(...obj.rotation)),
          new THREE.Vector3(...obj.scale),
        )
        .invert();
      const localOffset = new THREE.Vector3(px, py, pz).applyMatrix4(invMat);

      const vertCount = obj.vertices.length / 3;
      const newVerts = new Float32Array(obj.vertices);
      for (let i = 0; i < vertCount; i++) {
        newVerts[i * 3] += localOffset.x;
        newVerts[i * 3 + 1] += localOffset.y;
        newVerts[i * 3 + 2] += localOffset.z;
      }

      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
          vertices: newVerts,
          loopNormals: meshRecomputeLoopNormals(
            newVerts,
            obj.topology,
            resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
          ),
          position: [0, 0, 0] as [number, number, number],
        }),
      });
    },

    /**
     * Move the object's origin to an explicit world-space point (e.g. the
     * 3D cursor) without shifting the mesh visually — counter-shifts all
     * vertices in object-local space so the geometry stays put. Mirrors
     * `setOriginToWorld` and `setOriginToGeometry` but takes a target point.
     */
    setOriginToPosition: (
      objectId: string,
      worldPoint: [number, number, number],
    ) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;
      const patch = originToPositionPatch(obj, worldPoint);
      if (!patch) return;
      get().pushUndo();
      set({
        sceneObjects: patchSceneObject(get().sceneObjects, objectId, patch),
      });
    },

    /**
     * Duplicate selection in place and enter translate mode. Single entry point
     * for both object-mode and edit-mode duplication — all UI surfaces route
     * through here so they remain mode-agnostic.
     *
     * Owns pushUndo internally. Early-returns (without polluting undo history)
     * when nothing is selected in the active scope.
     */
    duplicate: () => {
      const s = get();
      if (s.editMode) {
        if (!hasVertexSelection(s.editMode)) return;
        s.pushUndo();
        duplicateSelectionInEditMode(get, set, s.editMode);
      } else {
        if (s.selectedObjectTargets.length === 0) return;
        s.pushUndo();
        duplicateSelectedObjects(get, set);
      }
      get().enterCommand("translate");
    },

    addToLibrary: (objectId: string) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj) return;

      const entry: LibraryEntry = {
        id: createLibraryEntryId(),
        name: obj.name,
        createdAt: Date.now(),
        vertices: new Float32Array(obj.vertices),
        indices: cloneRenderTriangleIndices(obj.indices),
        normals: new Float32Array(obj.loopNormals),
        topology: {
          offsets: new Uint32Array(obj.topology.offsets),
          corners: new Uint32Array(obj.topology.corners),
          edges: new Uint32Array(obj.topology.edges),
          cornerEdges: new Uint32Array(obj.topology.cornerEdges),
          edgeFlags: new Uint8Array(obj.topology.edgeFlags),
        },
        uvs: obj.uvs ? new Float32Array(obj.uvs) : undefined,
        material: {
          ...(get().materials.get(getPrimaryMaterialId(obj))?.properties ??
            DEFAULT_MATERIAL),
        },
      };

      const library = [...get().objectLibrary, entry];
      set({ objectLibrary: library });
      saveLibrary(library);
    },

    removeFromLibrary: (entryId: string) => {
      const library = get().objectLibrary.filter((e) => e.id !== entryId);
      set({ objectLibrary: library });
      saveLibrary(library);
    },

    spawnFromLibrary: (entryId: string) => {
      const entry = get().objectLibrary.find((e) => e.id === entryId);
      if (!entry) return;

      const existing = [...get().sceneObjects.values()].filter((o) =>
        o.name.startsWith(entry.name),
      ).length;
      const name =
        existing === 0
          ? entry.name
          : `${entry.name}.${String(existing + 1).padStart(3, "0")}`;

      const obj: SceneObject = {
        id: createSceneObjectId(),
        name,
        objectKind: "imported",
        position: cursorSpawnPosition(get().cursor3D),
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        vertices: new Float32Array(entry.vertices),
        indices: asRenderTriangleIndices(new Uint32Array(entry.indices)),
        loopNormals: asLoopNormals(new Float32Array(entry.normals)),
        topology: {
          offsets: new Uint32Array(entry.topology.offsets),
          corners: new Uint32Array(entry.topology.corners),
          edges: new Uint32Array(entry.topology.edges),
          cornerEdges: new Uint32Array(entry.topology.cornerEdges),
          edgeFlags: new Uint8Array(entry.topology.edgeFlags),
        },
        uvs: entry.uvs ? new Float32Array(entry.uvs) : undefined,
        materialSlots: createDefaultMaterialSlots(
          get().createMaterial(entry.material),
        ),
        shading: "smooth",
      };

      const dupAdd = addSceneObject(get().sceneObjects, obj);
      if (!dupAdd.admitted) return;
      clearLiveEditPreview();
      set({
        sceneObjects: dupAdd.map,
        selectedObjectTargets: [{ kind: "sceneObject", id: obj.id }],
        mode: "transform" as InteractionMode,
        editMode: null,
      });
    },

    joinObjects: () => {
      const state = get();
      const { sceneObjects, editMode } = state;
      if (editMode) return; // disallow in edit mode

      // Filter to joinable scene objects (mesh geometry, not lights/mannequins)
      const selectedIds = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        state.selectedObjectTargets,
      );
      const selected = [...sceneObjects.values()].filter(
        (o) =>
          selectedIds.has(o.id) &&
          !isLightObject(o) &&
          !isMannequinObject(o) &&
          !isImagePlaneObject(o),
      );
      if (selected.length < 2) return;

      get().pushUndo();

      // Target is the first selected object (Set preserves insertion order)
      const targetId = [...selectedIds].find((id) =>
        selected.some((o) => o.id === id),
      )!;
      const target = selected.find((o) => o.id === targetId)!;
      const sources = selected.filter((o) => o.id !== targetId);

      // Build the target's inverse world matrix
      const targetMat = new THREE.Matrix4().compose(
        new THREE.Vector3(...target.position),
        new THREE.Quaternion().setFromEuler(
          new THREE.Euler(...target.rotation),
        ),
        new THREE.Vector3(...target.scale),
      );
      const targetInverse = targetMat.clone().invert();

      // Transform each source's vertices into target-local space
      const meshes = [{ vertices: target.vertices, topology: target.topology }];
      const v = new THREE.Vector3();

      for (const src of sources) {
        const srcMat = new THREE.Matrix4().compose(
          new THREE.Vector3(...src.position),
          new THREE.Quaternion().setFromEuler(new THREE.Euler(...src.rotation)),
          new THREE.Vector3(...src.scale),
        );
        const composite = targetInverse.clone().multiply(srcMat);

        const transformed = new Float32Array(src.vertices);
        const vertCount = transformed.length / 3;
        for (let i = 0; i < vertCount; i++) {
          v.set(
            transformed[i * 3],
            transformed[i * 3 + 1],
            transformed[i * 3 + 2],
          );
          v.applyMatrix4(composite);
          transformed[i * 3] = v.x;
          transformed[i * 3 + 1] = v.y;
          transformed[i * 3 + 2] = v.z;
        }
        meshes.push({ vertices: transformed, topology: src.topology });
      }

      const merged = concatMeshes(meshes);
      const loopNormals = meshRecomputeLoopNormals(
        merged.vertices,
        merged.topology,
        resolveAutoSmoothAngle(target.shading, target.autoSmoothAngle),
      );
      const sourceIds = new Set(sources.map((o) => o.id));

      // Provenance: target's verts come first in `concatMeshes` and survive
      // at identity indices; appended source verts are novel in target's
      // vertex space (provenance is per-object, sources are different
      // objects). `inheritPerVertexAttributes` then projects target's
      // per-vertex attributes (vertex groups, skin radii, sculpt data)
      // onto the joined mesh — appended verts get the natural defaults
      // (group memberships empty, skin radius 1.0, etc.).
      const targetVertCount = target.vertices.length / 3;
      const totalVertCount = merged.vertices.length / 3;
      const joinProvenance = provenanceFromJoinAppend(
        targetVertCount,
        totalVertCount - targetVertCount,
      );
      const mergedIndices = asRenderTriangleIndices(merged.indices);
      const joinedScaffold: SceneObject = {
        ...target,
        vertices: merged.vertices,
        indices: mergedIndices,
        loopNormals,
        topology: merged.topology,
        uvs: unwrapBoxProject(merged.vertices, loopNormals, merged.topology),
      };
      const joinedObject = inheritPerVertexAttributes(
        target,
        joinedScaffold,
        joinProvenance,
      );

      // Remove sources, replace target with merged object
      const newSceneObjects = new Map(sceneObjects);
      for (const id of sourceIds) newSceneObjects.delete(id);
      newSceneObjects.set(targetId, joinedObject);

      // Clean up state maps for removed objects
      const newVisibility = new Map(get().visibility);
      const newRenderVisibility = new Map(get().renderVisibility);
      const newModifierOverrides = new Map(get().modifierOverrides);
      const newMannequinMorphs = new Map(get().mannequinMorphs);
      for (const id of sourceIds) {
        newVisibility.delete(id);
        newRenderVisibility.delete(id);
        newModifierOverrides.delete(id);
        newMannequinMorphs.delete(id);
      }

      // Drop projected strokes attached to merged-source objects (their
      // bind-pose coords no longer match the joined topology) and bump
      // the target's topologyVersion so its strokes drop too — corner
      // indices into the joined geometry are stale.
      const target0 = sceneObjects.get(targetId);
      const newTopologyVersion = (target0?.topologyVersion ?? 0) + 1;
      const targetWithBumpedVersion = newSceneObjects.get(targetId);
      if (targetWithBumpedVersion) {
        newSceneObjects.set(targetId, {
          ...targetWithBumpedVersion,
          topologyVersion: newTopologyVersion,
        });
      }
      const sketch = pruneSceneSketch(get().sketch, {
        removedObjectIds: sourceIds,
        topologyBumped: new Map([[targetId, newTopologyVersion]]),
      });

      clearLiveEditPreview();
      set({
        sceneObjects: newSceneObjects,
        selectedObjectTargets: [{ kind: "sceneObject", id: targetId }],
        visibility: newVisibility,
        renderVisibility: newRenderVisibility,
        modifierOverrides: newModifierOverrides,
        mannequinMorphs: newMannequinMorphs,
        sketch,
      });
    },

    mergeObjects: () => {
      const state = get();
      const { sceneObjects, editMode } = state;
      if (editMode) return;

      const selectedIds = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        state.selectedObjectTargets,
      );
      const selected = [...sceneObjects.values()].filter(
        (o) =>
          selectedIds.has(o.id) &&
          !isLightObject(o) &&
          !isMannequinObject(o) &&
          !isImagePlaneObject(o),
      );
      if (selected.length < 2) return;

      get().pushUndo();

      const targetId = [...selectedIds].find((id) =>
        selected.some((o) => o.id === id),
      )!;
      const target = selected.find((o) => o.id === targetId)!;
      const sources = selected.filter((o) => o.id !== targetId);

      const targetMat = new THREE.Matrix4().compose(
        new THREE.Vector3(...target.position),
        new THREE.Quaternion().setFromEuler(
          new THREE.Euler(...target.rotation),
        ),
        new THREE.Vector3(...target.scale),
      );
      const targetInverse = targetMat.clone().invert();

      const meshes = [{ vertices: target.vertices, topology: target.topology }];
      const v = new THREE.Vector3();

      for (const src of sources) {
        const srcMat = new THREE.Matrix4().compose(
          new THREE.Vector3(...src.position),
          new THREE.Quaternion().setFromEuler(new THREE.Euler(...src.rotation)),
          new THREE.Vector3(...src.scale),
        );
        const composite = targetInverse.clone().multiply(srcMat);

        const transformed = new Float32Array(src.vertices);
        const vertCount = transformed.length / 3;
        for (let i = 0; i < vertCount; i++) {
          v.set(
            transformed[i * 3],
            transformed[i * 3 + 1],
            transformed[i * 3 + 2],
          );
          v.applyMatrix4(composite);
          transformed[i * 3] = v.x;
          transformed[i * 3 + 1] = v.y;
          transformed[i * 3 + 2] = v.z;
        }
        meshes.push({ vertices: transformed, topology: src.topology });
      }

      const joined = concatMeshes(meshes);

      // Weld coincident vertices across the joined mesh. Select all verts so
      // merge-by-distance operates on the entire combined mesh, not a subset.
      const allVerts = new Set<number>();
      const vertCount = joined.vertices.length / 3;
      for (let i = 0; i < vertCount; i++) allVerts.add(i);

      const weldResult = meshMergeByDistance(
        new Float32Array(joined.vertices), // clone — mergeByDistance mutates for centroid
        joined.indices,
        joined.topology,
        allVerts,
        0.0001,
      );

      const finalTopo = weldResult ? weldResult.topology : joined.topology;
      const finalVerts = weldResult ? weldResult.vertices : joined.vertices;
      // Derive corner-based render indices from topology — triangulateTopology()
      // returns shared vertex indices (for topology ops), but the renderer
      // expects corner indices from triangulateTopologyForRendering().
      const finalGeom = {
        vertices: finalVerts,
        indices: triangulateTopologyForRendering(finalTopo),
        topology: finalTopo,
      };

      const loopNormals = meshRecomputeLoopNormals(
        finalGeom.vertices,
        finalGeom.topology,
        resolveAutoSmoothAngle(target.shading, target.autoSmoothAngle),
      );

      // Provenance: stage 1 is the same target-identity-plus-novel-sources
      // shape as `joinObjects`. Stage 2 is the weld provenance returned by
      // `meshMergeByDistance` (which now carries one). Compose to get a
      // single canonical OLD-target-vert → FINAL-merged-vert mapping; the
      // join-novel source verts inherit nothing (no parents in target's
      // space), and any target verts that welded into source positions
      // remain represented through the compose chain.
      const targetVertCount = target.vertices.length / 3;
      const joinedVertCount = joined.vertices.length / 3;
      const stage1 = provenanceFromJoinAppend(
        targetVertCount,
        joinedVertCount - targetVertCount,
      );
      const mergeProvenance = weldResult
        ? composeProvenance(stage1, weldResult.provenance)
        : stage1;
      const mergedScaffold: SceneObject = {
        ...target,
        vertices: finalGeom.vertices,
        indices: finalGeom.indices,
        loopNormals,
        topology: finalGeom.topology,
        uvs: unwrapBoxProject(
          finalGeom.vertices,
          loopNormals,
          finalGeom.topology,
        ),
      };
      const mergedObject = inheritPerVertexAttributes(
        target,
        mergedScaffold,
        mergeProvenance,
      );

      const sourceIds = new Set(sources.map((o) => o.id));
      const newSceneObjects = new Map(sceneObjects);
      for (const id of sourceIds) newSceneObjects.delete(id);
      const newTopologyVersion = (target.topologyVersion ?? 0) + 1;
      newSceneObjects.set(targetId, {
        ...mergedObject,
        topologyVersion: newTopologyVersion,
      });

      const newVisibility = new Map(get().visibility);
      const newRenderVisibility = new Map(get().renderVisibility);
      const newModifierOverrides = new Map(get().modifierOverrides);
      const newMannequinMorphs = new Map(get().mannequinMorphs);
      for (const id of sourceIds) {
        newVisibility.delete(id);
        newRenderVisibility.delete(id);
        newModifierOverrides.delete(id);
        newMannequinMorphs.delete(id);
      }

      const sketch = pruneSceneSketch(get().sketch, {
        removedObjectIds: sourceIds,
        topologyBumped: new Map([[targetId, newTopologyVersion]]),
      });

      set({
        sceneObjects: newSceneObjects,
        selectedObjectTargets: [{ kind: "sceneObject", id: targetId }],
        visibility: newVisibility,
        renderVisibility: newRenderVisibility,
        modifierOverrides: newModifierOverrides,
        mannequinMorphs: newMannequinMorphs,
        sketch,
      });
    },

    renameSceneObject: (id: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed) return;

      const obj = get().sceneObjects.get(id);
      if (!obj || obj.name === trimmed) return;

      get().pushUndo();
      const modifierOverrides = retargetModifierOverridesObjectName(
        get().modifierOverrides,
        obj.name,
        trimmed,
      );

      set({
        sceneObjects: patchSceneObject(get().sceneObjects, id, {
          name: trimmed,
        }),
        ...(modifierOverrides ? { modifierOverrides } : {}),
      });
    },

    applyModifier: (objectId: string, modifierIndex: number) => {
      const obj = get().sceneObjects.get(objectId);
      if (!obj || obj.vertices.length === 0) return;

      const allOverrides = get().modifierOverrides;
      const overrides = allOverrides.get(objectId) ?? [];
      const mergedMods = mergeModifierStack([], overrides);
      const sceneObjectsByName = buildSceneObjectNameMap(get().sceneObjects);
      if (modifierIndex < 0 || modifierIndex >= mergedMods.length) return;

      // Follow Object constraint — bake by freezing the current visual transform
      // into the stored position/rotation/scale, then removing the modifier.
      const modToApply = mergedMods[modifierIndex];
      if (modToApply.kind === "followObject") {
        const allObjs = get().sceneObjects;
        let [px, py, pz] = obj.position;
        let [rx, ry, rz] = obj.rotation;
        let [sx, sy, sz] = obj.scale;

        if (modToApply.enabled && modToApply.targetObjectName) {
          let target: SceneObject | undefined;
          for (const o of allObjs.values()) {
            if (o.name === modToApply.targetObjectName) {
              target = o;
              break;
            }
          }
          if (target) {
            const tmpPos = new THREE.Vector3();
            const tmpQuat = new THREE.Quaternion();
            const tmpQuat2 = new THREE.Quaternion();
            const tmpEuler = new THREE.Euler();
            const tmpScale = new THREE.Vector3();

            if (modToApply.targetBoneName) {
              const bone = getBone(target.id, modToApply.targetBoneName);
              if (bone) {
                if (modToApply.followPosition) {
                  bone.getWorldPosition(tmpPos);
                  px += tmpPos.x;
                  py += tmpPos.y;
                  pz += tmpPos.z;
                }
                if (modToApply.followRotation) {
                  bone.getWorldQuaternion(tmpQuat);
                  tmpEuler.set(rx, ry, rz);
                  tmpQuat2.setFromEuler(tmpEuler);
                  tmpQuat.multiply(tmpQuat2);
                  tmpEuler.setFromQuaternion(tmpQuat);
                  rx = tmpEuler.x;
                  ry = tmpEuler.y;
                  rz = tmpEuler.z;
                }
                if (modToApply.followScale) {
                  bone.getWorldScale(tmpScale);
                  sx *= tmpScale.x;
                  sy *= tmpScale.y;
                  sz *= tmpScale.z;
                }
              }
            } else {
              if (modToApply.followPosition) {
                px += target.position[0];
                py += target.position[1];
                pz += target.position[2];
              }
              if (modToApply.followRotation) {
                tmpEuler.set(
                  target.rotation[0],
                  target.rotation[1],
                  target.rotation[2],
                );
                tmpQuat.setFromEuler(tmpEuler);
                tmpEuler.set(rx, ry, rz);
                tmpQuat2.setFromEuler(tmpEuler);
                tmpQuat.multiply(tmpQuat2);
                tmpEuler.setFromQuaternion(tmpQuat);
                rx = tmpEuler.x;
                ry = tmpEuler.y;
                rz = tmpEuler.z;
              }
              if (modToApply.followScale) {
                sx *= target.scale[0];
                sy *= target.scale[1];
                sz *= target.scale[2];
              }
            }
          }
        }

        get().pushUndo();

        // Remove only this modifier override; modifiers above it in the stack are preserved.
        const newOverrides = [...overrides];
        newOverrides.splice(modifierIndex, 1);
        const modOverrides = new Map(get().modifierOverrides);
        if (newOverrides.length === 0) {
          modOverrides.delete(objectId);
        } else {
          modOverrides.set(objectId, newOverrides);
        }

        set({
          sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
            position: [px, py, pz] as [number, number, number],
            rotation: [rx, ry, rz] as [number, number, number],
            scale: [sx, sy, sz] as [number, number, number],
          }),
          modifierOverrides: modOverrides,
        });
        return;
      }

      // Apply modifiers up to and including the target index.
      // Build ModifierGeometry directly so polygon topology flows through the
      // pipeline — quads and ngons are preserved in the baked result instead of
      // being reconstructed as triangles from the output index buffer.
      const modsToApply = mergedMods.slice(0, modifierIndex + 1);
      const inputGeo: ModifierGeometry = buildSceneObjectModifierGeometry(obj);
      const runtimeSources = runtimeMannequinModifierSources(
        modsToApply,
        sceneObjectsByName,
      );
      void (async () => {
        const result = await evaluateModifierStackAsync(
          buildModifierEvaluationRequest({
            baseCacheKey: sceneObjectGeometryCacheKey(obj),
            geometry: inputGeo,
            owner: {
              position: obj.position,
              rotation: obj.rotation,
              scale: obj.scale,
              vertexGroups: obj.vertexGroups,
            },
            modifiers: modsToApply,
            sceneObjectsByName,
            runtimeSourceGeometries: runtimeSources.runtimeSourceGeometries,
            runtimeSourceCacheKeys: runtimeSources.runtimeSourceCacheKeys,
            modifierOverrides: allOverrides,
          }),
        );

        const geometryPatch = modifierGeometryBakePatch(obj, result);

        const newOverrides = overrides.slice(modifierIndex + 1);

        get().pushUndo();

        const modOverrides = new Map(get().modifierOverrides);
        if (newOverrides.length === 0) {
          modOverrides.delete(objectId);
        } else {
          modOverrides.set(objectId, newOverrides);
        }

        set({
          sceneObjects: patchSceneObject(
            get().sceneObjects,
            objectId,
            geometryPatch,
          ),
          modifierOverrides: modOverrides,
        });
      })().catch((error) => {
        console.error("Failed to apply modifier:", error);
      });
    },

    deleteCutterCollectionCuts: (collectionId: string) => {
      const state = get();
      const collection = state.collections.get(collectionId);
      const cutterRef = collection
        ? resolveCollectionCutterRef(
            collection,
            state.sceneObjects,
            state.modifierOverrides,
          )
        : null;
      if (!collection || !cutterRef) return;

      const patch = buildDeleteCutterObjectsPatch(
        state,
        collectionId,
        cutterRef.cutterObjectIds,
        { selectHostAfterDelete: true },
      );
      if (!patch) return;
      state.pushUndo();
      set(patch);
    },

    deleteCutterCollectionCut: (
      collectionId: string,
      cutterObjectId: string,
    ) => {
      const state = get();
      const patch = buildDeleteCutterObjectsPatch(
        state,
        collectionId,
        new Set([cutterObjectId]),
      );
      if (!patch) return;
      state.pushUndo();
      set(patch);
    },

    bakeCutterCollection: async (collectionId: string) => {
      const state = get();
      const collection = state.collections.get(collectionId);
      const cutterRef = collection
        ? resolveCollectionCutterRef(
            collection,
            state.sceneObjects,
            state.modifierOverrides,
          )
        : null;
      if (!collection || !cutterRef) return;

      const hostObject = state.sceneObjects.get(cutterRef.hostObjectId);
      if (!hostObject || hostObject.vertices.length === 0) return;

      const cutterObjectIds = new Set(cutterRef.cutterObjectIds);
      if (cutterObjectIds.size === 0) return;

      const sourceSceneObjects = state.sceneObjects;
      const sourceCollections = state.collections;
      const sourceModifierOverrides = state.modifierOverrides;
      const hostOverrideRef = sourceModifierOverrides.get(hostObject.id);
      const hostOverrides = hostOverrideRef ?? [];
      const modifiers = mergeModifierStack([], hostOverrides);
      const hostWorld =
        getSceneObjectWorldTransform(
          sourceSceneObjects,
          sourceCollections,
          hostObject.id,
        ) ?? sceneObjectEulerTransform(hostObject);

      const preservedMemberWorldTransforms = new Map<string, EulerTransform>();
      for (const objectId of collection.objectIds) {
        if (objectId === hostObject.id || cutterObjectIds.has(objectId)) {
          continue;
        }
        const world = getSceneObjectWorldTransform(
          sourceSceneObjects,
          sourceCollections,
          objectId,
        );
        if (world) preservedMemberWorldTransforms.set(objectId, world);
      }

      let geometryPatch: Partial<SceneObject> = {};
      if (modifiers.some((modifier) => modifier.enabled)) {
        const inputGeo = buildSceneObjectModifierGeometry(hostObject);
        const sceneObjectsByName = buildSceneObjectWorldNameMap(
          sourceSceneObjects,
          sourceCollections,
        );
        const runtimeSources = runtimeMannequinModifierSources(
          modifiers,
          sceneObjectsByName,
        );

        try {
          const result = await evaluateModifierStackAsync(
            buildModifierEvaluationRequest({
              baseCacheKey: sceneObjectGeometryCacheKey(hostObject),
              geometry: inputGeo,
              owner: {
                position: hostWorld.position,
                rotation: hostWorld.rotation,
                scale: hostWorld.scale,
                vertexGroups: hostObject.vertexGroups,
              },
              modifiers,
              sceneObjectsByName,
              runtimeSourceGeometries: runtimeSources.runtimeSourceGeometries,
              runtimeSourceCacheKeys: runtimeSources.runtimeSourceCacheKeys,
              modifierOverrides: sourceModifierOverrides,
            }),
          );
          geometryPatch = modifierGeometryBakePatch(hostObject, result);
        } catch (error) {
          console.error("Failed to bake cutter collection:", error);
          return;
        }
      }

      const latest = get();
      if (
        latest.sceneObjects !== sourceSceneObjects ||
        latest.collections !== sourceCollections ||
        latest.modifierOverrides !== sourceModifierOverrides ||
        latest.sceneObjects.get(hostObject.id) !== hostObject ||
        latest.collections.get(collectionId) !== collection ||
        latest.modifierOverrides.get(hostObject.id) !== hostOverrideRef
      ) {
        return;
      }

      latest.pushUndo();

      const removedObjectIds = new Set(cutterObjectIds);
      removedObjectIds.delete(hostObject.id);

      const sceneObjects = new Map(latest.sceneObjects);
      sceneObjects.set(hostObject.id, {
        ...hostObject,
        ...geometryPatch,
        position: hostWorld.position,
        rotation: hostWorld.rotation,
        scale: hostWorld.scale,
        generator: undefined,
      });
      for (const [objectId, world] of preservedMemberWorldTransforms) {
        const obj = sceneObjects.get(objectId);
        if (!obj) continue;
        sceneObjects.set(objectId, {
          ...obj,
          position: world.position,
          rotation: world.rotation,
          scale: world.scale,
        });
      }
      for (const objectId of removedObjectIds) {
        sceneObjects.delete(objectId);
      }

      const collections = new Map(latest.collections);
      collections.delete(collectionId);

      const modifierOverrides = new Map(latest.modifierOverrides);
      modifierOverrides.delete(hostObject.id);
      for (const objectId of removedObjectIds) {
        modifierOverrides.delete(objectId);
      }

      const visibility = new Map(latest.visibility);
      const renderVisibility = new Map(latest.renderVisibility);
      const modifierUICollapsed = new Map(latest.modifierUICollapsed);
      modifierUICollapsed.delete(hostObject.id);
      for (const objectId of removedObjectIds) {
        visibility.delete(objectId);
        renderVisibility.delete(objectId);
        modifierUICollapsed.delete(objectId);
      }

      set({
        sceneObjects,
        collections,
        modifierOverrides,
        visibility,
        renderVisibility,
        modifierUICollapsed,
        selectedObjectTargets: [{ kind: "sceneObject", id: hostObject.id }],
        mode: "transform" as InteractionMode,
        editMode:
          latest.editMode &&
          (latest.editMode.objectId === hostObject.id ||
            removedObjectIds.has(latest.editMode.objectId))
            ? null
            : latest.editMode,
        activeEditSession:
          latest.editMode &&
          (latest.editMode.objectId === hostObject.id ||
            removedObjectIds.has(latest.editMode.objectId))
            ? null
            : latest.activeEditSession,
      });
    },
  };
}

// ---------------------------------------------------------------------------
// duplicateSelectedObjects — object-mode duplicate worker
//
// Clones each selected SceneObject in place (position/rotation/scale preserved)
// and re-keys every piece of per-object state the store holds in side-maps
// under the new id. This matches Blender's Shift+D semantics: the duplicate
// is indistinguishable from the source except for its identity.
//
// Per-object state carried over:
//   • geometry + material (the SceneObject itself, deep-cloned)
//   • visibility / renderVisibility
//   • shadingOverrides
//   • modifierOverrides (the modifier stack)
//   • mannequinMorphs, mannequinBonePoses, boneVolumes
//   • lockedBones, pinnedBones
//   • constraintWeights
//
// Deliberately NOT carried over:
//   • editSessionCache — transient UI state (hidden geometry, xray, etc.);
//     a fresh duplicate starts with a clean edit session.
// ---------------------------------------------------------------------------
function duplicateSelectedObjects(get: Get_, set: Set_): void {
  const state = get();
  const {
    sceneObjects,
    visibility,
    renderVisibility,
    shadingOverrides,
    modifierOverrides,
    mannequinMorphs,
    mannequinBonePoses,
    boneVolumes,
    lockedBones,
    pinnedBones,
    constraintWeights,
  } = state;

  const selectedIds = selectionToLeafObjectIds(
    state.blendFiles,
    state.collections,
    state.selectedObjectTargets,
  );
  const sources = [...sceneObjects.values()].filter((o) =>
    selectedIds.has(o.id),
  );

  const newIds = new Set<string>();
  const existingNames = new Set([...sceneObjects.values()].map((o) => o.name));

  // Start each side-map as a copy of the current state; the loop then writes
  // cloned entries under the new ids without touching the source entries.
  const newVisibility = new Map(visibility);
  const newRenderVisibility = new Map(renderVisibility);
  const newShadingOverrides = new Map(shadingOverrides);
  const newModifierOverrides = new Map(modifierOverrides);
  const newMorphs = new Map(mannequinMorphs);
  const newBonePoses = new Map(mannequinBonePoses);
  const newBoneVolumes = new Map(boneVolumes);
  const newLockedBones = new Map(lockedBones);
  const newPinnedBones = new Map(pinnedBones);
  const newConstraintWeights = new Map(constraintWeights);

  const newObjects: SceneObject[] = sources.map((src) => {
    const id = createSceneObjectId();
    newIds.add(id);

    // Strip existing .NNN suffix to get base name, then find the next free slot
    const baseName = src.name.replace(/\.\d{3}$/, "");
    let suffix = 1;
    let name: string;
    do {
      name = `${baseName}.${String(suffix).padStart(3, "0")}`;
      suffix++;
    } while (existingNames.has(name));
    existingNames.add(name);

    // Flat scalar/boolean maps — copy by value.
    const vis = visibility.get(src.id);
    if (vis !== undefined) newVisibility.set(id, vis);
    const renderVis = renderVisibility.get(src.id);
    if (renderVis !== undefined) newRenderVisibility.set(id, renderVis);
    const shading = shadingOverrides.get(src.id);
    if (shading !== undefined) newShadingOverrides.set(id, shading);
    const cw = constraintWeights.get(src.id);
    if (cw !== undefined) newConstraintWeights.set(id, cw);

    // Modifier stack — deep-clone each ModifierOverride (mirrors cloneOverrides
    // in storeUndo.ts: shallow-spread the override, clone its props and any
    // runtimeModifier).
    const mods = modifierOverrides.get(src.id);
    if (mods) {
      newModifierOverrides.set(
        id,
        mods.map((o) => ({
          ...o,
          props: o.props ? { ...o.props } : undefined,
          runtimeModifier: o.runtimeModifier
            ? { ...o.runtimeModifier }
            : undefined,
        })),
      );
    }

    // Mannequin state — nested maps/sets, deep-cloned.
    const morphs = mannequinMorphs.get(src.id);
    if (morphs) newMorphs.set(id, new Map(morphs));
    const poses = mannequinBonePoses.get(src.id);
    if (poses) {
      newBonePoses.set(
        id,
        new Map(
          [...poses].map(([k, v]) => [
            k,
            [...v] as [number, number, number, number],
          ]),
        ),
      );
    }
    const vols = boneVolumes.get(src.id);
    if (vols) newBoneVolumes.set(id, new Map(vols));
    const locked = lockedBones.get(src.id);
    if (locked) newLockedBones.set(id, new Set(locked));
    const pinned = pinnedBones.get(src.id);
    if (pinned) {
      newPinnedBones.set(
        id,
        new Map(
          [...pinned].map(([k, v]) => [k, [...v] as [number, number, number]]),
        ),
      );
    }

    return {
      ...src,
      id,
      name,
      position: [...src.position] as [number, number, number],
      rotation: [...src.rotation] as [number, number, number],
      scale: [...src.scale] as [number, number, number],
      vertices: new Float32Array(src.vertices),
      indices: cloneRenderTriangleIndices(src.indices),
      loopNormals: asLoopNormals(new Float32Array(src.loopNormals)),
      topology: {
        offsets: new Uint32Array(src.topology.offsets),
        corners: new Uint32Array(src.topology.corners),
        edges: new Uint32Array(src.topology.edges),
        cornerEdges: new Uint32Array(src.topology.cornerEdges),
        edgeFlags: new Uint8Array(src.topology.edgeFlags),
      },
      uvs: src.uvs ? new Float32Array(src.uvs) : undefined,
      materialSlots: src.materialSlots.map((s) => ({ ...s })),
    };
  });

  const batchAdd = addSceneObjects(get().sceneObjects, newObjects);
  if (!batchAdd.admitted) return;
  clearLiveEditPreview();
  set({
    sceneObjects: batchAdd.map,
    selectedObjectTargets: [...newIds].map((id) => ({
      kind: "sceneObject",
      id,
    })),
    visibility: newVisibility,
    renderVisibility: newRenderVisibility,
    shadingOverrides: newShadingOverrides,
    modifierOverrides: newModifierOverrides,
    mannequinMorphs: newMorphs,
    mannequinBonePoses: newBonePoses,
    boneVolumes: newBoneVolumes,
    lockedBones: newLockedBones,
    pinnedBones: newPinnedBones,
    constraintWeights: newConstraintWeights,
    mode: "transform" as InteractionMode,
    editMode: null,
  });
}
